import { useAllCompanies } from "http-hooks/company/useCompany";
import DeleteCompany from "components/company/DeleteCompany";
import { useState } from "react";
import Modal from "components/ui/modals";
import useModalStore from "store/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import { Badge } from "components/ui/Table";
import SetCompany from "components/company/SetCompany";
import MigrateCompany from "components/company/MigrateCompany";

// Constants moved to top to prevent re-renders
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  company_name: "",
  ag_status: "",
  page: 1,
  limit: 10,
};

export default function CompanyMaster() {
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [migrateCompany, setMigrateCompany] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const { openModal, isModalOpen, getModalEditId, closeModal } = useModalStore();

  const openSetModal = (id) => {
    openModal('company', id);
  };

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, company_name: searchTerm, page: 1 }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFilter((prev) => ({
      ...prev,
      ag_status: selectedOption?.value || "",
      page: 1,
    }));
  };

  const clearFilters = () => {
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const {
    isLoading: isAllCompaniesLoading,
    data: { data: allcompanies, page: currentPage, totalPages } = [],
  } = useAllCompanies(filter);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {deleteCompanyId && (
        <Modal open={!!deleteCompanyId} setOpen={(val) => !val && setDeleteCompanyId(null)}>
          <DeleteCompany
            id={deleteCompanyId}
            onClose={() => setDeleteCompanyId(null)}
          />
        </Modal>
      )}

      {isModalOpen('company') && (
        <Modal open={isModalOpen('company')} setOpen={() => closeModal('company')} maxWidth="4xl">
          <SetCompany
            id={getModalEditId('company')}
            onClose={() => closeModal('company')}
          />
        </Modal>
      )}

      {migrateCompany && (
        <Modal open={!!migrateCompany} setOpen={(val) => !val && setMigrateCompany(null)} maxWidth="2xl">
          <MigrateCompany
            companyId={migrateCompany.id}
            companyName={migrateCompany.name}
            onClose={() => setMigrateCompany(null)}
          />
        </Modal>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Companies
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all registered companies, their databases and preferences
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => openSetModal()}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-purple-600 px-3.5 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-colors"
          >
            <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            New Company
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3">
          <InputSearch
            onSearch={handleSearch}
            label="Company Name"
            placeholder="Search Company Name"
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            label="Status"
            placeholder="Select status"
            isClearable={true}
            isSearchable={false}
          />
        </div>

        <div className="bg-white rounded-lg p-3 flex items-end">
          <button
            type="button"
            onClick={clearFilters}
            className="w-full rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <Table>
          <THead>
            <TR>
              <TH first>ID</TH>
              <TH>Company Name</TH>
              <TH>Email</TH>
              <TH>Mobile</TH>
              <TH>Acc Type</TH>
              <TH>Subscription</TH>
              <TH>Turnover</TH>
              <TH>Status</TH>
              <TH last>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {isAllCompaniesLoading ? (
              <LoadingRow colSpan={9} message="Loading companies..." />
            ) : allcompanies?.length === 0 ? (
              <EmptyRow colSpan={9} message="No companies found." />
            ) : (
              allcompanies?.map((company) => (
                <TR key={company.id_company}>
                  <TD first>{company.id_company}</TD>
                  <TD>
                    <div className="font-medium text-gray-900">{company.company_name}</div>
                    {company.contact_person && (
                      <div className="text-xs text-gray-500">{company.contact_person}</div>
                    )}
                  </TD>
                  <TD>{company.company_email_id || "-"}</TD>
                  <TD>{company.contact_no || "-"}</TD>
                  <TD>
                    <Badge variant={company.acctype === 1 ? "info" : "primary"}>
                      {company.acctype === 1 ? "Trader" : "Agent"}
                    </Badge>
                  </TD>
                  <TD>
                    <Badge variant={company.subscription_type === "P" ? "warning" : "default"}>
                      {company.subscription_type === "P" ? "Plus" : "General"}
                    </Badge>
                  </TD>
                  <TD>{company.turnover_limit ? Number(company.turnover_limit).toLocaleString("en-IN") : "0"}</TD>
                  <TD>
                    {company.ag_status === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    )}
                  </TD>
                  <TD last>
                    <ActionButton
                      variant="secondary"
                      onClick={() => openSetModal(company.id_company)}
                      title="Edit Company"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </ActionButton>
                    <ActionButton
                      variant="primary"
                      onClick={() => setMigrateCompany({ id: company.id_company, name: company.company_name })}
                      title="Migrate Data"
                    >
                      <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => setDeleteCompanyId(company.id_company)}
                      title="Delete Company"
                      className="mr-0"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </ActionButton>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPageJump={true}
      />
    </div>
  );
}
